import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Party } from '../lib/types';
import {
  Card,
  EmptyState,
  ErrorNote,
  PageHead,
  PartyCell,
  Spinner,
  Tag,
} from '../components/ui';

const KIND_LABEL: Record<string, string> = {
  SME: 'Suppliers',
  PAYER: 'Payers',
  LENDER: 'Funders',
};

export function PartiesPage() {
  const query = useQuery({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/parties'),
  });

  if (query.isLoading) return <Spinner label="Loading counterparties" />;
  if (query.error) return <ErrorNote error={query.error} />;

  const parties = query.data ?? [];
  const kinds: Party['kind'][] = ['SME', 'PAYER', 'LENDER'];

  return (
    <>
      <PageHead
        crumb="Reference"
        title="Counterparties"
        sub="Funding is blocked on any deal whose supplier or payer is not KYC cleared."
      />

      {kinds.map((kind) => {
        const rows = parties.filter((p) => p.kind === kind);
        return (
          <div key={kind} style={{ marginBottom: 22 }}>
            <div className="section-title" style={{ marginTop: 0 }}>
              {KIND_LABEL[kind]}
            </div>
            <Card padded={false}>
              {rows.length === 0 ? (
                <EmptyState title={`No ${KIND_LABEL[kind]!.toLowerCase()} yet`} />
              ) : (
                <div className="table-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Sector</th>
                        <th>Country</th>
                        <th>Grade</th>
                        <th>Currency</th>
                        <th>KYC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p) => (
                        <tr key={p.id} style={{ cursor: 'default' }}>
                          <td>
                            <PartyCell party={p} />
                          </td>
                          <td className="cell-t2">{p.sector ?? '—'}</td>
                          <td className="cell-t2">{p.country ?? '—'}</td>
                          <td className="num">{p.creditGrade ?? '—'}</td>
                          <td className="cell-t2">{p.currency}</td>
                          <td>
                            <Tag tone={p.kycStatus === 'CLEARED' ? 'positive' : 'warning'}>
                              {p.kycStatus}
                            </Tag>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        );
      })}
    </>
  );
}
